// client/src/pages/admin/Users.tsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Tag,
  Avatar,
  Dropdown,
  Modal,
  Form,
  Select,
  Switch,
  message,
  Typography,
  DatePicker,
  Row,
  Col
} from 'antd';
import {
  UserOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  MoreOutlined,
  UserAddOutlined,
  MailOutlined,
  LockOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  IdcardOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService, User, CreateUserData, UpdateUserData } from '../../services/userService';
import dayjs from 'dayjs';

const { Search } = Input;
const { Option } = Select;
const { Text, Title } = Typography;

const AdminUsers: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState<'admin' | 'driver' | ''>('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | ''>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // États pour les modals
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [driverInfoModalVisible, setDriverInfoModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  // Formulaires
  const [userForm] = Form.useForm();
  const [driverForm] = Form.useForm();
  const [editForm] = Form.useForm();
  
  // État temporaire pour stocker les données user avant d'ajouter les données driver
  const [pendingUserData, setPendingUserData] = useState<any>(null);
  
  const queryClient = useQueryClient();

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  // Fetch users
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', debouncedSearchText, roleFilter, statusFilter, currentPage, pageSize],
    queryFn: () => userService.getUsers({
      search: debouncedSearchText,
      role: roleFilter || undefined,
      is_active: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
      page: currentPage,
      limit: pageSize
    }),
  });

  const users = usersData?.data || [];
  const pagination = usersData?.pagination;

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: (data: CreateUserData) => userService.createUser(data),
    onSuccess: () => {
      message.success('Utilisateur créé avec succès');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      handleCloseAllModals();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la création');
    },
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserData }) =>
      userService.updateUser(id, data),
    onSuccess: () => {
      message.success('Utilisateur mis à jour avec succès');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setEditModalVisible(false);
      setSelectedUser(null);
      editForm.resetFields();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la mise à jour');
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => userService.deleteUser(id),
    onSuccess: () => {
      message.success('Utilisateur supprimé avec succès');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Erreur lors de la suppression');
    },
  });

  // ========================================
  // HANDLERS
  // ========================================

  // Fermer toutes les modals et réinitialiser les états
  const handleCloseAllModals = () => {
    setCreateModalVisible(false);
    setDriverInfoModalVisible(false);
    setPendingUserData(null);
    userForm.resetFields();
    driverForm.resetFields();
  };

  // Étape 1: Soumettre le formulaire User
  const handleUserFormSubmit = async (values: any) => {
    const selectedRole = values.role;

    if (selectedRole === 'driver') {
      // Si c'est un driver, stocker les données et ouvrir la 2ème modal
      setPendingUserData(values);
      setCreateModalVisible(false);
      setDriverInfoModalVisible(true);
    } else {
      // Sinon, créer directement l'utilisateur (admin)
      await createUserMutation.mutateAsync(values);
    }
  };

  // Étape 2: Soumettre le formulaire Driver (avec les données User)
  const handleDriverFormSubmit = async (driverValues: any) => {
    if (!pendingUserData) {
      message.error('Erreur: données utilisateur manquantes');
      return;
    }

    // Debug: afficher les valeurs reçues
    console.log('📋 Données driver du formulaire:', driverValues);
    console.log('👤 Données user en attente:', pendingUserData);

    // Combiner les données User + Driver
    const completeData = {
      ...pendingUserData,
      // Données driver supplémentaires
      license_number: driverValues.license_number,
      license_categories: driverValues.license_categories,
      license_expiry: driverValues.license_expiry?.format('YYYY-MM-DD'),
      date_of_birth: driverValues.date_of_birth?.format('YYYY-MM-DD'),
      address: driverValues.address,
      emergency_contact_name: driverValues.emergency_contact_name,
      emergency_contact_phone: driverValues.emergency_contact_phone,
      hire_date: driverValues.hire_date?.format('YYYY-MM-DD') || dayjs().format('YYYY-MM-DD')
    };

    console.log('📤 Données complètes envoyées à l\'API:', completeData);

    // Validation côté client avant envoi
    if (!completeData.license_number) {
      message.error('Le numéro de permis est requis');
      return;
    }
    if (!completeData.license_categories || completeData.license_categories.length === 0) {
      message.error('Au moins une catégorie de permis est requise');
      return;
    }
    if (!completeData.license_expiry) {
      message.error('La date d\'expiration du permis est requise');
      return;
    }
    if (!completeData.date_of_birth) {
      message.error('La date de naissance est requise');
      return;
    }
    if (!completeData.address) {
      message.error('L\'adresse est requise');
      return;
    }
    if (!completeData.emergency_contact_name) {
      message.error('Le nom du contact d\'urgence est requis');
      return;
    }
    if (!completeData.emergency_contact_phone) {
      message.error('Le téléphone du contact d\'urgence est requis');
      return;
    }

    await createUserMutation.mutateAsync(completeData);
  };

  // Retour à l'étape 1 (User)
  const handleBackToUserForm = () => {
    setDriverInfoModalVisible(false);
    setCreateModalVisible(true);
  };

  // Edit user
  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    editForm.setFieldsValue({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      phone: user.phone,
      role: user.role,
      is_active: user.is_active
    });
    setEditModalVisible(true);
  };

  // Update user
  const handleUpdateUser = async (values: any) => {
    if (!selectedUser) return;
    await updateUserMutation.mutateAsync({
      id: selectedUser.id,
      data: values
    });
  };

  // Delete user
  const handleDeleteUser = (id: string) => {
    Modal.confirm({
      title: 'Confirmer la suppression',
      content: 'Êtes-vous sûr de vouloir supprimer cet utilisateur ?',
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: () => deleteUserMutation.mutateAsync(id)
    });
  };

  // ========================================
  // TABLE COLUMNS
  // ========================================

  const columns: ColumnsType<User> = [
    {
      title: 'Utilisateur',
      key: 'user',
      render: (_, user) => (
        <Space>
          <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
          <div>
            <div style={{ fontWeight: 500 }}>
              {user.first_name} {user.last_name}
            </div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {user.email}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Rôle',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={role === 'admin' ? 'blue' : 'green'}>
          {role === 'admin' ? 'Administrateur' : 'Chauffeur'}
        </Tag>
      ),
    },
    {
      title: 'Téléphone',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => phone || '-',
    },
    {
      title: 'Statut',
      key: 'status',
      render: (_, user) => (
        <Tag color={user.is_active ? 'success' : 'default'}>
          {user.is_active ? 'Actif' : 'Inactif'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, user) => {
        const items = [
          {
            key: 'view',
            icon: <EyeOutlined />,
            label: 'Voir',
          },
          {
            key: 'edit',
            icon: <EditOutlined />,
            label: 'Modifier',
            onClick: () => handleEditUser(user),
          },
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Supprimer',
            danger: true,
            onClick: () => handleDeleteUser(user.id),
          },
        ];

        return (
          <Dropdown
            menu={{
              items: items.map(item => ({
                ...item,
                style: item.danger ? { color: '#ff4d4f' } : undefined
              }))
            }}
            trigger={['click']}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  // ========================================
  // RENDER
  // ========================================

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={4} style={{ margin: 0 }}>
              <UserOutlined /> Gestion des utilisateurs
            </Title>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
            >
              Nouvel utilisateur
            </Button>
          </div>

          {/* Filters */}
          <Space size="middle" wrap>
            <Search
              placeholder="Rechercher..."
              allowClear
              style={{ width: 250 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Select
              placeholder="Filtrer par rôle"
              allowClear
              style={{ width: 150 }}
              value={roleFilter || undefined}
              onChange={(value) => setRoleFilter(value || '')}
            >
              <Option value="admin">Administrateur</Option>
              <Option value="driver">Chauffeur</Option>
            </Select>
            <Select
              placeholder="Filtrer par statut"
              allowClear
              style={{ width: 150 }}
              value={statusFilter || undefined}
              onChange={(value) => setStatusFilter(value || '')}
            >
              <Option value="active">Actif</Option>
              <Option value="inactive">Inactif</Option>
            </Select>
          </Space>

          {/* Table */}
          <Table
            columns={columns}
            dataSource={users}
            rowKey="id"
            loading={isLoading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: pagination?.total || 0,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} utilisateurs`,
              onChange: (page, size) => {
                setCurrentPage(page);
                if (size) setPageSize(size);
              },
            }}
          />
        </Space>
      </Card>

      {/* ========================================
          MODAL 1: Créer User (Étape 1)
          ======================================== */}
      <Modal
        title={
          <Space>
            <UserAddOutlined />
            <span>Créer un utilisateur - Étape 1/1</span>
          </Space>
        }
        open={createModalVisible}
        onCancel={handleCloseAllModals}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form
          form={userForm}
          layout="vertical"
          onFinish={handleUserFormSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="first_name"
                label="Prénom"
                rules={[{ required: true, message: 'Le prénom est requis' }]}
              >
                <Input placeholder="Jean" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="last_name"
                label="Nom"
                rules={[{ required: true, message: 'Le nom est requis' }]}
              >
                <Input placeholder="Dupont" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "L'email est requis" },
              { type: 'email', message: "Format d'email invalide" }
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="jean.dupont@exemple.com" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Mot de passe"
            rules={[
              { required: true, message: 'Le mot de passe est requis' },
              { min: 8, message: 'Au moins 8 caractères' }
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Mot de passe" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Rôle"
            rules={[{ required: true, message: 'Le rôle est requis' }]}
          >
            <Select placeholder="Sélectionner un rôle">
              <Option value="admin">Administrateur</Option>
              <Option value="driver">Chauffeur</Option>
            </Select>
          </Form.Item>

          <Form.Item name="phone" label="Téléphone">
            <Input placeholder="+33123456789" />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Statut"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Actif" unCheckedChildren="Inactif" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCloseAllModals}>
                Annuler
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createUserMutation.isPending}
                icon={
                  userForm.getFieldValue('role') === 'driver' ? 
                  <ArrowRightOutlined /> : 
                  <PlusOutlined />
                }
              >
                {userForm.getFieldValue('role') === 'driver' ? 'Suivant' : 'Créer'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ========================================
          MODAL 2: Informations Chauffeur (Étape 2)
          ======================================== */}
      <Modal
        title={
          <Space>
            <IdcardOutlined />
            <span>Informations du chauffeur - Étape 2/2</span>
          </Space>
        }
        open={driverInfoModalVisible}
        onCancel={handleCloseAllModals}
        footer={null}
        width={700}
        destroyOnClose
      >
        <Form
          form={driverForm}
          layout="vertical"
          onFinish={handleDriverFormSubmit}
        >
          {/* Section Permis */}
          <Title level={5}>📄 Permis de conduire</Title>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="license_number"
                label="Numéro de permis"
                rules={[{ required: true, message: 'Le numéro de permis est requis' }]}
              >
                <Input placeholder="123456789012" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="license_expiry"
                label="Date d'expiration"
                rules={[{ required: true, message: 'La date d\'expiration est requise' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder="JJ/MM/AAAA"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="license_categories"
            label="Catégories de permis"
            rules={[{ required: true, message: 'Au moins une catégorie est requise' }]}
          >
            <Select mode="multiple" placeholder="Sélectionner les catégories">
              <Option value="B">B - Véhicules légers</Option>
              <Option value="C">C - Poids lourds</Option>
              <Option value="CE">CE - Poids lourds + remorque</Option>
              <Option value="C1">C1 - Camions légers</Option>
              <Option value="D">D - Transport de personnes</Option>
            </Select>
          </Form.Item>

          {/* Section Informations personnelles */}
          <Title level={5} style={{ marginTop: 24 }}>👤 Informations personnelles</Title>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="date_of_birth"
                label="Date de naissance"
                rules={[{ required: true, message: 'La date de naissance est requise' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder="JJ/MM/AAAA"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="hire_date" label="Date d'embauche">
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder="JJ/MM/AAAA"
                  defaultValue={dayjs()}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="address"
            label="Adresse"
            rules={[{ required: true, message: 'L\'adresse est requise' }]}
          >
            <Input.TextArea
              rows={2}
              placeholder="123 Rue de la République, 75001 Paris"
            />
          </Form.Item>

          {/* Section Contact d'urgence */}
          <Title level={5} style={{ marginTop: 24 }}>🚨 Contact d'urgence</Title>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="emergency_contact_name"
                label="Nom du contact"
                rules={[{ required: true, message: 'Le nom du contact est requis' }]}
              >
                <Input placeholder="Marie Dupont" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="emergency_contact_phone"
                label="Téléphone"
                rules={[{ required: true, message: 'Le téléphone est requis' }]}
              >
                <Input placeholder="+33123456789" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right', marginTop: 24 }}>
            <Space>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={handleBackToUserForm}
              >
                Retour
              </Button>
              <Button onClick={handleCloseAllModals}>
                Annuler
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={createUserMutation.isPending}
                icon={<UserAddOutlined />}
              >
                Créer le chauffeur
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ========================================
          MODAL: Modifier utilisateur
          ======================================== */}
      <Modal
        title="Modifier l'utilisateur"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedUser(null);
          editForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateUser}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="first_name"
                label="Prénom"
                rules={[{ required: true, message: 'Le prénom est requis' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="last_name"
                label="Nom"
                rules={[{ required: true, message: 'Le nom est requis' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "L'email est requis" },
              { type: 'email', message: "Format d'email invalide" }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="phone" label="Téléphone">
            <Input />
          </Form.Item>

          <Form.Item
            name="role"
            label="Rôle"
            rules={[{ required: true, message: 'Le rôle est requis' }]}
          >
            <Select>
              <Option value="admin">Administrateur</Option>
              <Option value="driver">Chauffeur</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Statut"
            valuePropName="checked"
          >
            <Switch checkedChildren="Actif" unCheckedChildren="Inactif" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setEditModalVisible(false);
                setSelectedUser(null);
                editForm.resetFields();
              }}>
                Annuler
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={updateUserMutation.isPending}
              >
                Mettre à jour
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminUsers;